import { Dispatch, ReactElement, SetStateAction } from 'react';
import * as React from 'react';

import { Modal } from '@utmb/design-system/modal/Modal';
import { HorizontalAlignEnum, VerticalAlignEnum } from '@utmb/types/modal';
import MyModalFooter from '@utmb/features/my-modal-footer/MyModalFooter';
import { DashboardMemberWorldSerieRegistrationView } from '@utmb/types/index';
import styles from './registration-group-modal.module.scss';
import { Font } from '@utmb/design-system/text/font';
import { getCategoryColor, getColorStyle } from '@utmb/utils/colors';
import { ColorTypesEnum } from '@utmb/types/colors';
import { MyTable } from '@utmb/design-system/my-table/MyTable';
import { MyTableHeader } from '@utmb/design-system/my-table/MyTableHeader';
import { MyTableCell } from '@utmb/design-system/my-table/MyTableCell';
import { MyTableRow } from '@utmb/design-system/my-table/MyTableRow';
import { PiCategoryLogo } from '@utmb/design-system/pi-category-logo/PiCategoryLogo';
import { IconsEnum } from '@utmb/types/icons';
import { toast } from 'react-toastify';
import { useTranslation } from 'next-i18next';
import { ButtonRemake } from '@utmb/design-system/button-remake/ButtonRemake';
import RunningStones from '@utmb/design-system/running-stone/RunningStones';
import { Icon } from '@utmb/design-system/icons/Icon';

interface RegistrationGroupModalProps {
    isOpen: boolean;
    close: Dispatch<SetStateAction<boolean>>;
    data: DashboardMemberWorldSerieRegistrationView;
}

export const RegistrationGroupModal = ({ isOpen, close, data }: RegistrationGroupModalProps): ReactElement | null => {
    const { t } = useTranslation();

    function handleGrpCopy() {
        navigator.clipboard.writeText(data.grp);
        toast.success(t('races.preregistration.groupNumberCopied'));
    }

    if (!data.otherGroupMembers) return null;

    const isBoost = data.hasBoost || data.otherGroupMembers?.find((target) => target.hasBoost);
    const isStones = data.otherGroupMembers?.find((target) => typeof target.stoneNumber === 'number');

    return (
        <Modal isOpen={isOpen} onClose={close} vAlign={VerticalAlignEnum.TOP} hAlign={HorizontalAlignEnum.RIGHT}>
            <div className={styles.content}>
                <div className={styles.raceTitle}>
                    <Font.OxaniumBold className={styles.title} as="span" desktop="30" mobile="22" color={getCategoryColor(data.category)}>
                        {data.raceName}
                    </Font.OxaniumBold>
                    <PiCategoryLogo piCategory={data.category} height={20} isDark={true} />
                </div>

                <div className={styles.groupTitle}>
                    <Font.FuturaMedium className={styles.title} color={getColorStyle(ColorTypesEnum.PRIMARY)} desktop="20" mobile="20">
                        {`${t('races.preregistration.groupNumberTitle')} : `}
                    </Font.FuturaMedium>

                    <ButtonRemake
                        customClassName={styles.copyButton}
                        action={() => handleGrpCopy()}
                        label={data.grp}
                        icon={IconsEnum.CLIPBOARD}
                        variant="transparent"
                        noTranslate
                    />
                </div>

                <MyTable className={styles.table}>
                    <MyTableHeader className={styles.table_header}>
                        <MyTableCell column={3}>
                            <Font.FuturaBold color={getColorStyle(ColorTypesEnum.PRIMARY)} className={styles.headerText} desktop="16" mobile="16">
                                {t('breadcrumbs.runners')}
                            </Font.FuturaBold>
                        </MyTableCell>

                        {
                            (isStones ? (
                                <MyTableCell column={1}>
                                    <RunningStones size={40} />
                                </MyTableCell>
                            ) : (
                                false
                            )) as false | React.ReactElement
                        }

                        {
                            (isBoost ? (
                                <MyTableCell column={1}>
                                    <Font.FuturaBold color={getColorStyle(ColorTypesEnum.PRIMARY)} className={styles.headerText} desktop="16" mobile="16">{`${t(
                                        'races.preregistration.boost'
                                    )} ${data.boostPct}%`}</Font.FuturaBold>
                                </MyTableCell>
                            ) : (
                                false
                            )) as false | React.ReactElement
                        }
                    </MyTableHeader>

                    {data.otherGroupMembers.map((member) => (
                        <MyTableRow key={`${member.name}`} className={styles.customTableRow} height={50} showDetailsClassName={styles.customShowDetailTableRow}>
                            <MyTableCell column={3}>
                                <Font.FuturaBold noTranslate desktop="16" mobile="16" color={getColorStyle(ColorTypesEnum.PRIMARY)}>
                                    {member.name}
                                </Font.FuturaBold>
                            </MyTableCell>

                            {isStones ? (
                                <MyTableCell column={1}>
                                    <Font.FuturaMedium desktop="16" mobile="16" color={getColorStyle(ColorTypesEnum.PRIMARY)}>
                                        {member.stoneNumber}
                                    </Font.FuturaMedium>
                                </MyTableCell>
                            ) : (
                                (false as false | React.ReactElement)
                            )}

                            {isBoost ? (
                                <MyTableCell column={1}>
                                    <Font.FuturaMedium desktop="16" mobile="16" color={getColorStyle(ColorTypesEnum.PRIMARY)}>
                                        {member.hasBoost ? (
                                            <Icon width={28} height={28} variant={IconsEnum.CHECKBOX} color={getColorStyle(ColorTypesEnum.PRIMARY)} />
                                        ) : null}
                                    </Font.FuturaMedium>
                                </MyTableCell>
                            ) : (
                                (false as false | React.ReactElement)
                            )}
                        </MyTableRow>
                    ))}
                </MyTable>
            </div>
            <MyModalFooter />
        </Modal>
    );
};
